
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import IconButton from '@/components/shared/suites/IconButton.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'orders';

const DEFAULT_PAYMENT_INFORMATION: PaymentInfo = {
	invoice: false,
	costCenter: '',
	purchaseOrder: ''
};

@Component<PaymentInformation>({
	components: {
		IconButton,
		SelectComponent,
		TextField,
		ValidationProvider
	}
})
export default class PaymentInformation extends Vue {
	@Action('setPaymentInformation', { namespace }) private setPaymentInformation!: (paymentInfo: PaymentInfo) => void;
	@Getter('getPaymentInformation', { namespace }) private paymentInformation!: PaymentInfo;
	@Getter('getModifyingOrder', { namespace }) private isModifyingOrder!: boolean;
	@Getter('getExtraPaymentMethods', { namespace: 'auth' }) private extraPaymentMethods!: RestaurantExtraPaymentMethod[];
	@Prop({ type: Boolean, default: false }) private hideHeader!: boolean;

	private paymentMethod: string = '';
	private mutablePaymentInformation: PaymentInfo = DEFAULT_PAYMENT_INFORMATION;
	private additionalPaymentInfoLocked: boolean = true;

	/**
	 * Get localized payment options based on the restaurant's extra payment methods.
	 *
	 * @return {RestaurantExtraPaymentMethod[]}
	 */
	private get paymentOptions(): RestaurantExtraPaymentMethod[] {
		const paymentMethodsMap: Record<string, string> = {
			'invoice': 'invoice',
			'cost_center': 'costCenter',
			'purchase_order': 'purchaseOrder'
		};

		if (!this.isModifyingOrder) {
			if (!this.extraPaymentMethods || !Array.isArray(this.extraPaymentMethods)) {
				return [];
			}
			return this.extraPaymentMethods.map((method: RestaurantExtraPaymentMethod) => {
				return {
					label: Vue.filter('localize')(method, 'label') || this.$t(`orders.management.order_modal.payment_preferences.${method.type}`),
					type: paymentMethodsMap[method.type]
				};
			});
		}

		if (!this.paymentMethod) {
			return [];
		}

		// When modifying orders, only show the payment method that was used when placing the order, since payment method cannot be changed during modification
		// This covers an edge case when the order's payment method has been removed from the restaurant's extra payment methods before modification
		// Credit card is not part of the extra payment methods so we handle it differently
		if (this.paymentMethod === 'creditCard') {
			return [{
				label: this.$t('orders.management.order_modal.payment_preferences.credit_card'),
				type: this.paymentMethod
			}];
		}

		const paymentMethodKey = Object.entries(paymentMethodsMap)?.find(([, value]) => value === this.paymentMethod)?.[0];
		const orderPaymentMethod = this.extraPaymentMethods?.find(method => method.type === paymentMethodKey);
		const paymentLabel = orderPaymentMethod ? Vue.filter('localize')(orderPaymentMethod, 'label') : this.$t(`orders.management.order_modal.payment_preferences.${paymentMethodKey}`);

		return [{
			label: paymentLabel,
			type: this.paymentMethod
		}];
	}

	private get paymentMethodLabel(): string | undefined {
		return this.paymentOptions?.find(method => method.type === this.paymentMethod)?.label || '';
	}

	private created(): void {
		this.mutablePaymentInformation = { ...this.paymentInformation };

		// If only one payment method is available and not modifying order, set it as the default
		// Otherwise check if the payment method is already set in existing order data
		if (this.paymentOptions.length === 1 && !this.isModifyingOrder) {
			this.paymentMethod = this.paymentOptions[0].type;
			this.handlePaymentMethodChange();
		}
		else {
			this.paymentMethod = Object.keys(this.mutablePaymentInformation).find(key => this.mutablePaymentInformation[key as keyof PaymentInfo]) || '';
		}
	}

	/**
	 * Handle payment method selection
	 * Reset payment information to prevent creating an order with multiple payment methods
	 * Only the invoice payment method is set here, since other methods require additional information
	 *
	 * @return {void}
	 */
	private handlePaymentMethodChange(): void {
		this.mutablePaymentInformation = {
			...DEFAULT_PAYMENT_INFORMATION,
			invoice: this.paymentMethod === 'invoice'
		};
		this.setPaymentInformation(this.mutablePaymentInformation);
	}
}
